// ============================================================================
// NOTICE TEMPLATE HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { noticeTemplateService } from '../../api/services/noticeTemplateService';
import type {
  NoticeTemplate,
  NoticeTemplateListItem,
  CreateNoticeTemplateRequest,
  UpdateNoticeTemplateRequest,
  NoticeTemplateSearchRequest,
} from '../../api/types/noticeTemplate.types';
import type { ID } from '../../api/types';

export const useNoticeTemplates = (params?: NoticeTemplateSearchRequest) => {
  const [data, setData] = useState<NoticeTemplateListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1, pageSize: 20, totalPages: 0, totalItems: 0,
    hasNextPage: false, hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchItems = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await noticeTemplateService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  return { data, loading, error, pagination, refetch: fetchItems };
};

export const useNoticeTemplate = (id: ID | undefined) => {
  const [data, setData] = useState<NoticeTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchItem = useCallback(async () => {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      const response = await noticeTemplateService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchItem(); }, [fetchItem]);

  return { data, loading, error, refetch: fetchItem };
};

export const useCreateNoticeTemplate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createNoticeTemplate = async (
    data: CreateNoticeTemplateRequest,
    options?: { onSuccess?: (item: NoticeTemplate) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await noticeTemplateService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { createNoticeTemplate, loading, error };
};

export const useUpdateNoticeTemplate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateNoticeTemplate = async (
    id: ID, data: UpdateNoticeTemplateRequest,
    options?: { onSuccess?: (item: NoticeTemplate) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await noticeTemplateService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { updateNoticeTemplate, loading, error };
};

export const useDeleteNoticeTemplate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteNoticeTemplate = async (
    id: ID,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await noticeTemplateService.delete(id);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e); options?.onError?.(e); throw e;
    } finally { setLoading(false); }
  };

  return { deleteNoticeTemplate, loading, error };
};
